// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CheckoutButton from "@rugby/App/Components/CheckoutButton";
import Notify from "@rugby/App/Components/Notify";
import TippyPopup from "@rugby/App/Components/Tippy/TippyPopup";
import { xHeaderState } from "@rugby/App/Store/Reducers";
import { getCloudinarySecureUrl, getDefaultImagePath } from "@rugby/App/Store/Selectors/GeneralSettings";
import LazyImage from "@rugby/MobiusOverrides/LazyImage";
import ProductPrice from "@rugby/Overrides/Components/ProductPrice";
import { miniCartStyles } from "@rugby/Overrides/Widgets/Common/MiniCart.style";
import { cloudinaryUrl } from "@rugby/XDL/Helpers";
import CartIcon from "@rugby/XDL/Icons/CartIcon";
import Trash from "@rugby/XDL/Icons/Trash";
import { isMobileDevice } from "@rugby/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    const cart = getCurrentCartState(state).value;
    const checkoutIsDisabled =
        !canCheckoutWithCart(cart) || hasOnlyQuoteRequiredProducts(cart) || isCartCheckoutDisabled(cart);

    return {
        checkoutIsDisabled,
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        checkoutUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cartLines: getCurrentCartState(state).value?.cartLines || [],
        cart,
        isSticky: state.xHeader.isSticky,
        defaultImagePath: getDefaultImagePath(state),
        cloudinarySecureUrl: getCloudinarySecureUrl(state),
        isPunchOutOrder: isPunchOutOrder(cart),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    doPunchOutCheckout,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;

    cart: any;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = miniCartStyles;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    checkoutUrl,
    totalCountDisplay,
    history,
    orderSubTotalDisplay,
    fields: { visibilityState },
    isCompactHeaderCart,
    theme,
    cartLines,
    cart,
    removeCartLine,
    updateCartLine,
    loadCurrentCart,
    isSticky,
    checkoutIsDisabled,
    defaultImagePath,
    cloudinarySecureUrl,
    isPunchOutOrder,
    doPunchOutCheckout,
}) => {
    const [isPopupVisible, setPopupVisibility] = useState(false);

    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive);
    const existLineNeedAdditionalInfo = cart?.properties?.existLineNeedAdditionalInfo;

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }


    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const onPopupVisibilityUpdate = (popupVisibility: boolean) => {
        setPopupVisibility(popupVisibility);
    };

    // Xng-Change: HSP-1083
    // Just moved the code from checkout button onClick into checkoutHandler
    // And called the punchout handler
    const checkoutHandler = () => {
        hideTippy();

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        
        history.push(`${cartUrl}`);
    };

    const cartLinkElement = (
        <Link
            {...styles.miniCartButton}
            onClick={() => {
                !isMobileDevice() && history.push(cartUrl ?? "");
            }}
        >
            {showIcon && <Icon {...styles.cartIcon} src={CartIcon} {...{ isActive: isPopupVisible }} />}

            {!isSticky && showLabel && (
                <StyledWrapper className="menu-icon-text">
                    <Typography {...styles.itemsNumber}>
                        {totalCountDisplay || 0} {translate("Items")}
                    </Typography>
                    <Typography {...styles.subTotal}>{orderSubTotalDisplay || 0} </Typography>
                </StyledWrapper>
            )}
        </Link>
    );

    const miniCartContent = (
        <StyledWrapper {...styles.wrapper}>
            {cartLines.length > 0 && (
                <>
                    <Typography as="span" {...styles.title}>
                        {translate("Cart Summary")}
                    </Typography>

                    <StyledWrapper {...styles.content}>
                        {cartLines?.map(line => (
                            <CartLineContext.Provider value={line} key={uniqueId()}>
                                <StyledWrapper {...styles.line}>
                                    <StyledWrapper {...styles.lineImage}>
                                        <Link
                                            href={line.productUri}
                                            onClick={() => {
                                                hideTippy();
                                            }}
                                        >
                                            <LazyImage
                                                src={cloudinaryUrl(line.smallImagePath, cloudinarySecureUrl, 115)}
                                                altText={line.altText}
                                                defaultImagePath={defaultImagePath}
                                            />
                                        </Link>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.lineContent}>
                                        <Link
                                            {...styles.lineDescription}
                                            href={line.productUri}
                                            onClick={() => {
                                                hideTippy();
                                            }}
                                        >
                                            {line.shortDescription}
                                        </Link>
                                        <StyledWrapper {...styles.actionWrapper}>
                                            <StyledWrapper {...styles.linePriceUOMWrapper}>
                                                <ProductPrice
                                                    product={line}
                                                    showLabel={false}
                                                    showPriceUOM={true}
                                                    extendedStyles={styles.linePrice}
                                                />
                                            </StyledWrapper>

                                            <StyledWrapper {...styles.lineInput}>
                                                <CartLineQuantity
                                                    cart={cart as Cart}
                                                    editable={true}
                                                    label=" "
                                                    extendedStyles={styles.quantity}
                                                    onQtyOrderedChange={(inputQty: number) => {
                                                        qtyOrderedChangeHandler(line, inputQty);
                                                    }}
                                                />
                                            </StyledWrapper>

                                            <StyledWrapper {...styles.linePriceWrapper}>
                                                <ProductPrice
                                                    product={line}
                                                    showLabel={false}
                                                    extendedStyles={styles.linePrice}
                                                />
                                            </StyledWrapper>

                                            <StyledWrapper
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography {...styles.lineTotalPrice}>
                                                    {line?.pricing?.extendedUnitNetPriceDisplay}
                                                </Typography>

                                                <Clickable
                                                    onClick={() => {
                                                        removeCartLineClickHandler(line);
                                                    }}
                                                    data-test-selector="cartline_removeLine"
                                                >
                                                    <Icon {...styles.trashIcon} src={Trash}></Icon>
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </CartLineContext.Provider>
                        ))}
                    </StyledWrapper>

                    {!productsCannotBePurchased && cart.properties && cart.properties.existLineWithZeroPrice && (
                        <StyledWrapper {...styles.cartZeroPricedWrapper}>
                            <Notify
                                data-test-selector="cart_zeroPricedProduct"
                                body={cart.properties.existLineWithZeroPrice}
                                messageType="danger"
                            />
                        </StyledWrapper>
                    )}

                    {existLineNeedAdditionalInfo && (
                        <StyledWrapper {...styles.existLineNeedAdditionalInfo}>
                            <Notify
                                data-test-selector="cart_existLineNeedAdditionalInfo"
                                body={existLineNeedAdditionalInfo}
                                messageType="danger"
                            />
                        </StyledWrapper>
                    )}
                    {productsCannotBePurchased && (
                        <StyledWrapper {...styles.cartProductsCannotBePurchased}>
                            <Notify
                                data-test-selector="cart_productsCannotBePurchased"
                                body={siteMessage("Cart_ProductsCannotBePurchased")}
                                messageType="danger"
                            />
                        </StyledWrapper>
                    )}

                    {/* Actions */}
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <CheckoutButton
                            {...styles.buttons}
                            sizeVariant="medium"
                            onClick={checkoutHandler}
                            disabled={checkoutIsDisabled}
                        >
                            {cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : isPunchOutOrder
                                ? siteMessage("Rugby_Cart_SubmitPunchOutOrder")
                                : translate("Checkout")}
                        </CheckoutButton>
                        <Button
                            variant="secondary"
                            sizeVariant="medium"
                            {...styles.buttons}
                            onClick={() => {
                                hideTippy();
                                history.push(`${cartUrl}`);
                            }}
                            data-test-selector="headerSignOutLink"
                        >
                            {translate("View Cart")}
                        </Button>
                    </StyledWrapper>
                </>
            )}

            {!cartLines.length && (
                <Notify
                    notifyContainerOverrides={css`
                        margin: auto;
                        max-width: calc(100% - 20px);
                    `}
                    messageType="info"
                    body={translate("Add some items to cart !")}
                    showCloseButton={false}
                />
            )}
        </StyledWrapper>
    );

    if (isCompactHeaderCart) {
        return (
            <Link
                {...cartLinkStyles.routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...cartLinkStyles.routerLink?.icon, iconProps: { src: ShoppingCart, ...cartLinkStyles.routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...cartLinkStyles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    return (
        <TippyPopup
            updateTriggerOptionsOnMobile
            content={miniCartContent}
            onPopupVisibilityUpdate={onPopupVisibilityUpdate}
        >
            {cartLinkElement}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withHistory(CartLink))),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            } /*
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            }, */,
        ],
        group: "Common",
    },
};
export default widgetModule;
